import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { BlackListService } from '../../services/blacklist.service';
import { AlertService } from '../../services/alert.service';
import Header from '../../components/layout/header.component';
import Asaid from '../../components/layout/asaid.component';
import Footer from '../../components/layout/footer.component';
import useBgPages from '../../hooks/use-bg-pages.hook';
import EditDataBlacklist from '../blacklist/modal/edit-data.modal';
import Datatable from '../../components/datatable/datatable.component';
import useRequest from '../../hooks/use-request.hook';
import LogData from '../../components/logs/log-data.component';
import Icon from '../../components/forms/icon.component';
import AddBlackList from './partial/add-blacklist.partial';
import EditBlackList from './partial/edit-blacklist.partial';
import ActionsDropdown from '../../components/datatable/actions-dropdown.component';
import DateRegister from '../../components/datatable/register-data.component';
import { useLocation, useNavigate } from 'react-router-dom';
import PrincipalCard from '../../components/cards/principal-card.component';
import { t } from 'i18next';
import { useSession } from '../../hooks/use-session.hook';
import { PROFILES } from '../../constants';

interface Data {
  _id: string;
  created_at: string;
  status: boolean;
  provider: string;
  name: string;
  priority: boolean;
}

const StatusBadge = ({ status }: { status: true | false }) => {
  switch (status) {
    case true:
      return (
        <span className="badge text-dark fs-6">
          <Icon icon="ki-check-circle" size="fs-1" type="primary" />
          {t('general.active')}
        </span>
      );
    case false:
      return (
        <span className="badge text-danger fs-6">
          <Icon icon="ki-minus-circle" size="fs-1" type="danger" />
          {t('general.inactive')}
        </span>
      );
    default:
      return <span className="badge badge-secondary">Unknown</span>;
  }
};

const StatusPriority = ({ priority }: { priority: true | false }) => {
  switch (priority) {
    case true:
      return (
        <span className="badge text-dark fs-6">
          <Icon icon="ki-shield" size="fs-1" type="primary" />
          Habilitada
        </span>
      );
    case false:
      return (
        <span className="badge text-danger fs-6">
          <Icon icon="ki-minus-circle" size="fs-1" type="danger" />
          Inhabilitada
        </span>
      );
    default:
      return <span className="badge badge-secondary">Unknown</span>;
  }
};

function BlackListPage() {
  useBgPages();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { session, selectedAccount } = useSession();
  const { call, isRequesting } = useRequest();
  const [totalBlackList, setTotalBlackList] = useState<number>(0);
  const [totalActives, settotalActives] = useState<number>(0);
  const [page, setPage] = useState('principal');
  const [rows, setRows] = useState<Data[]>([]);

  const dataStatistics = [
    { label: t('roles.my-roles'), total: totalBlackList, type: '', icon: 'ki-book-open' },
    {
      label: t('channels.wa.actives'),
      total: totalActives,
      type: 'primary',
      icon: 'ki-check-circle'
    }
  ];

  const [showModal, setShowModal] = useState({
    show: false,
    data: null
  });

  const handleShowModal = async (refresh: boolean) => {
    if (refresh) {
      await getBlackList();
    }
    setShowModal({ show: false, data: null });
  };

  const renderStatusCell = (row: Data) => <StatusBadge status={row.status} />;
  const renderPriorityCell = (row: Data) => <StatusPriority priority={row.priority} />;

  const columns = [
    {
      name: t('blacklist-page.name-registered'),
      sortable: true,
      selector: (r: any) => `${r.name} ${r.totalNumbers}`,
      width: 20,
      cell: (row: any) => {
        return (
          <li className="list-group-item">
            <div className="d-flex flex-column">
              <div className="w-100 mb-1">
                <div>
                  <span className="ms-1 fw-bolder fs-5">{row.name || t('general.no-info')}</span>
                </div>
                <div>
                  <span className="ms-1 text-primary fw-bolder fs-6">
                    {row.totalNumbers
                      ? `${row.totalNumbers.toLocaleString()} ${t('blacklist-page.numbers-registered')}`
                      : '0 ' + t('blacklist-page.numbers-registered')}
                  </span>
                </div>
              </div>
            </div>
          </li>
        );
      }
    },
    {
      name: 'Prioritaria',
      sortable: true,
      selector: (r: any) => `${r.priority}`,
      width: 13,
      cell: renderPriorityCell
    },
    ...(session.profile !== PROFILES.ROOT
      ? []
      : [
          {
            name: t('blacklist-page.associated-company'),
            sortable: true,
            selector: (r: any) => `${r.account?.company?.name} ${r.account.name}`,
            width: 14,
            cell: (row: any) => {
              return (
                <li className="list-group-item text-nowrap">
                  <div className="d-flex flex-column">
                    <div className="w-100 mb-1 d-flex align-items-center">
                      <strong className="fs-6">{t('blacklist-page.company-label')}:</strong>

                      {!row?.account?.company?.name ? (
                        <span className="ms-1 text-muted fw-semibold d-flex align-items-center fs-5">
                          <Icon
                            icon="ki-information"
                            size="fs-5"
                            className="me-1 text-danger fw-bolder"
                          />
                          <span>{t('general.no-info')}</span>
                        </span>
                      ) : (
                        <span className="ms-2 fw-semibold fs-6">{row.account.company.name}</span>
                      )}
                    </div>
                    <div className="w-100 d-flex align-items-center">
                      <strong className="fs-6">{t('blacklist-page.account-label')}:</strong>
                      {!row?.account?.name ? (
                        <span className="ms-1 text-muted fw-semibold d-flex align-items-center fs-5">
                          <Icon
                            icon="ki-information"
                            size="fs-5"
                            className="me-1 text-danger fw-bolder"
                          />
                          <span>{t('general.no-info')}</span>
                        </span>
                      ) : (
                        <span className="ms-2 fw-semibold fs-6">{row.account.name}</span>
                      )}
                    </div>
                  </div>
                </li>
              );
            }
          }
        ]),
    {
      name: t('channels.wa.status'),
      sortable: true,
      selector: (r: any) => `${r.status}`,
      width: 13,
      cell: renderStatusCell
    },
    {
      name: t('users-page.title-column-4'),
      sortable: true,
      selector: (r: any) => `${r.created_at}`,
      width: 20,
      cell: (row: any) => {
        return <DateRegister created_at={row.created_at} />;
      }
    },
    {
      name: t('general.acctions'),
      sortable: true,
      selector: (r: any) => `${r._id}`,
      exportable: false,
      width: 10,
      cell: (row: any) => {
        return (
          <>
            <ActionsDropdown
              textCustomEdit={t('blacklist-page.edit-numbers')}
              edit={() => {
                setPage('editBlackList');
                navigate('/blacklist/edit-blacklist', { state: { payload: row } });
              }}
              editData={() => setShowModal({ show: true, data: row })}
              textCustomEditData={t('blacklist-page.edit-name')}
              delete={() => handleDeleteBlackList(row._id, row.name, 'deleteBlackList-3794736274')}
            />
          </>
        );
      }
    }
  ];

  const getBlackList = async () => {
    try {
      let response = [];
      if (session.profile === PROFILES.ROOT) {
        response = await call(() => BlackListService.getBlackList());
        const blackListData: Data[] = response;
        settotalActives(blackListData.filter((channel) => channel.status === true).length);
        setTotalBlackList(blackListData.length);
        setRows(blackListData);
      } else if (session.profile === PROFILES.ADMIN) {
        response = await call(() =>
          BlackListService.getBlackListByAccount(selectedAccount.account_id)
        );
        const blackListData: Data[] = response;
        settotalActives(blackListData.filter((channel) => channel.status === true).length);
        setTotalBlackList(blackListData.length);
        setRows(blackListData);
      }
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const handleDeleteBlackList = async (id: string, name: string, buttonId: string) => {
    try {
      const confirmed = await AlertService.question(
        t('general.atention'),
        `${t('blacklist-page.delete-blacklist')}, ${name}`,
        null as any,
        'Eliminar'
      );
      if (!confirmed) {
        return;
      }
      await BlackListService.deleteBlackList(id);
      AlertService.toastSuccess(t('blacklist-page.success-delete-bl'));
      getBlackList();
      LogData(id, buttonId, window.location.href);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getBlackList();
      } catch (errorCode: any) {
        console.log('Error:', errorCode);
        AlertService.toastError(errorCode.error || errorCode);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    getBlackList();
  }, [page]);

  useEffect(() => {
    if (location.pathname.includes('/blacklist/add-blacklist')) {
      setPage('addBlackList');
    } else if (location.pathname.includes('/blacklist/edit-blacklist')) {
      setPage('editBlackList');
    } else {
      setPage('principal');
    }
  }, [location.pathname]);

  return (
    <>
      <div className="page d-flex flex-row flex-column-fluid">
        <Asaid />
        <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
          <Header />
          <div className="content fs-6 d-flex flex-column flex-column-fluid" id="kt_content">
            <div className="toolbar mb-10" id="kt_toolbar">
              <div className=" container-fluid  d-flex flex-stack flex-wrap flex-sm-nowrap"></div>
            </div>
            <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
              <div className="container-xxl">
                {page === 'principal' ? (
                  <>
                    <PrincipalCard
                      dataStatistics={dataStatistics}
                      btnCreate
                      onClick={() => {
                        setPage('addBlackList');
                        navigate('/blacklist/add-blacklist');
                      }}
                      btnRefresh
                      onRefresh={async () => {
                        await getBlackList();
                        AlertService.toastSuccess('¡ Éxito !, información actualizada.');
                      }}
                      title={t('general.register')}
                      titleBolder={t('asaid.link-blacklist')}
                      subtitle={t('blacklist-page.page-text')}
                      breadcrumbItems={[t('asaid.link-blacklist')]}
                      textButton={t('blacklist-page.add-blacklist')}
                      iconButton="ki-soft-2"
                    />

                    <div className="card mb-5 mb-xl-10">
                      <div className="card-header align-items-center border-0 mt-5 animate-fadeInUp animate-delay-2">
                        <h3 className="card-title align-items-start flex-column">
                          <span className="fw-bolder fw-bold fs-2">
                            {t('asaid.link-blacklist')}
                          </span>
                          <span className="text-gray-700 mt-2 fw-semibold fs-6">
                            {t('channels.wa.registred')}
                          </span>
                        </h3>
                        <div className="card-toolbar"></div>
                      </div>
                      <div className="card-body pt-1">
                        <Datatable columns={columns} data={rows} isLoading={isRequesting} />
                      </div>
                    </div>
                  </>
                ) : page === 'addBlackList' ? (
                  <AddBlackList setPage={setPage} />
                ) : page === 'editBlackList' ? (
                  <div>
                    <EditBlackList />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <Footer />
          <EditDataBlacklist
            show={showModal.show}
            onRequestClose={(refresh: any) => handleShowModal(refresh)}
            payload={showModal.data}
          />
        </div>
      </div>
    </>
  );
}

export default BlackListPage;
